import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import useHelpTarget from '../hooks/useHelpTarget';

/**
 * SnapshotHistoryPanel — collapsible panel showing folder snapshot history.
 *
 * Props:
 *   caseId      — UUID of the case
 *   onRestored  — () => void called after a successful restore (triggers tree refresh)
 */
export default function SnapshotHistoryPanel({ caseId, onRestored }) {
  const helpRef = useHelpTarget({
    slug: 'folder-snapshots-and-revert',
    label: 'Folder history',
    question: 'How does folder history work, and what does Restore actually do?',
  });
  const [expanded, setExpanded] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null); // snapshot id being restored
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!expanded) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/documents/case/${caseId}/folder-snapshots`);
      setSnapshots(data.snapshots || []);
    } catch (err) {
      setError('Failed to load history: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [caseId, expanded]);

  useEffect(() => { load(); }, [load]);

  async function handleRestore(snap) {
    const label = snap.label || `snapshot from ${formatDate(snap.created_at)}`;
    if (!window.confirm(`Restore to "${label}"?\n\nThis will move all documents back to their state at that time. The current state will be saved as a new snapshot first.`)) return;
    setRestoring(snap.id);
    setError(null);
    try {
      await api.post(`/documents/case/${caseId}/folder-snapshots/${snap.id}/restore`);
      onRestored?.();
      await load();
    } catch (err) {
      setError('Restore failed: ' + err.message);
    } finally {
      setRestoring(null);
    }
  }

  return (
    <div className="snap-panel" ref={helpRef}>
      <button
        className="snap-panel-toggle"
        onClick={() => setExpanded(e => !e)}
      >
        <span className="snap-panel-icon">{expanded ? '▾' : '▸'}</span>
        <span className="snap-panel-label">Folder History</span>
        {snapshots.length > 0 && <span className="snap-panel-count">{snapshots.length}</span>}
      </button>

      {expanded && (
        <div className="snap-panel-body">
          {loading && <div className="snap-loading">Loading…</div>}
          {error && <div className="snap-error">{error}</div>}

          {!loading && !error && snapshots.length === 0 && (
            <div className="snap-empty">No snapshots yet. They are created automatically when Arc organizes your folders.</div>
          )}

          {snapshots.map(snap => (
            <div key={snap.id} className="snap-row">
              <div className="snap-row-info">
                <span className="snap-row-label">{snap.label || 'Folder state'}</span>
                <span className="snap-row-meta">
                  {formatDate(snap.created_at)} · {snap.document_count} docs
                  {snap.created_by === 'arc' && <span className="snap-row-arc"> · Arc</span>}
                </span>
              </div>
              <button
                className="btn btn-sm btn-ghost snap-restore-btn"
                onClick={() => handleRestore(snap)}
                disabled={restoring === snap.id}
              >
                {restoring === snap.id ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = diffMs / (1000 * 60);
  const diffH = diffMs / (1000 * 60 * 60);
  const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffMin < 1) return `just now (${timeStr})`;
  if (diffH < 1) return `${Math.floor(diffMin)}m ago (${timeStr})`;
  if (diffH < 24) return `${Math.floor(diffH)}h ago (${timeStr})`;
  if (diffH < 48) return `yesterday ${timeStr}`;
  return `${d.toLocaleDateString()} ${timeStr}`;
}
